import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle } from 'lucide-react';
import { GlassCard, GlassBadge } from '@/design-system';
import { formatTeamWeeks, formatPercent } from '@/utils/formatters';
import type { SupplyDemandAnalysis } from '@/types';

interface SupplyDemandWidgetProps {
  data: SupplyDemandAnalysis;
  className?: string;
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'UNDER_CAPACITY':
      return 'text-green-400';
    case 'NEAR_CAPACITY':
      return 'text-amber-400';
    case 'OVER_CAPACITY':
      return 'text-red-400';
    default:
      return 'text-gray-400';
  }
};

const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
  switch (status) {
    case 'UNDER_CAPACITY':
      return 'success';
    case 'NEAR_CAPACITY':
      return 'warning';
    case 'OVER_CAPACITY':
      return 'danger';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'UNDER_CAPACITY':
      return 'Under Capacity';
    case 'NEAR_CAPACITY':
      return 'Near Capacity';
    case 'OVER_CAPACITY':
      return 'Over Capacity';
    default:
      return 'Unknown';
  }
};

const SupplyDemandWidget: React.FC<SupplyDemandWidgetProps> = ({ data, className }) => {
  const { supply, demand, gap, categoryCompliance } = data;

  // Calculate percentages for the bar
  const maxValue = Math.max(supply.totalTeamWeeks, demand.totalTeamWeeks);
  const supplyPercent = (supply.totalTeamWeeks / maxValue) * 100;
  const demandPercent = (demand.totalTeamWeeks / maxValue) * 100;

  // Status icon
  const StatusIcon = gap.status === 'UNDER_CAPACITY'
    ? CheckCircle
    : gap.status === 'NEAR_CAPACITY'
    ? AlertTriangle
    : TrendingDown;

  return (
    <GlassCard className={className}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Supply vs Demand</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Portfolio capacity analysis for {data.scenarioName}
            </p>
          </div>
          <GlassBadge variant={getStatusBadgeVariant(gap.status)}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {getStatusLabel(gap.status)}
          </GlassBadge>
        </div>

        {/* Main Supply/Demand Comparison */}
        <div className="space-y-4 mb-6">
          {/* Supply Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-foreground">Total Supply</span>
              </div>
              <span className="text-sm font-bold text-foreground">
                {formatTeamWeeks(supply.totalTeamWeeks)}
              </span>
            </div>
            <div className="h-4 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                style={{ width: `${supplyPercent}%` }}
              />
            </div>
          </div>

          {/* Demand Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-foreground">Total Demand</span>
              </div>
              <span className="text-sm font-bold text-foreground">
                {formatTeamWeeks(demand.totalTeamWeeks)}
              </span>
            </div>
            <div className="h-4 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  gap.status === 'OVER_CAPACITY'
                    ? 'bg-gradient-to-r from-red-500 to-red-400'
                    : 'bg-gradient-to-r from-blue-500 to-blue-400'
                }`}
                style={{ width: `${demandPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Gap Summary */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-[var(--bg-tertiary)]/50 rounded-lg mb-6">
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Gap</p>
            <p className={`text-xl font-bold mt-1 ${
              gap.totalGap > 0 ? 'text-red-400' : 'text-emerald-400'
            }`}>
              {gap.totalGap > 0 ? '+' : ''}{formatTeamWeeks(gap.totalGap)}
            </p>
          </div>
          <div className="text-center border-x border-[var(--border-primary)]">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Utilization</p>
            <p className={`text-xl font-bold mt-1 ${getStatusColor(gap.status)}`}>
              {formatPercent(gap.utilizationRate)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
            <p className={`text-xl font-bold mt-1 ${getStatusColor(gap.status)}`}>
              {gap.status === 'UNDER_CAPACITY' ? 'OK' : gap.status === 'NEAR_CAPACITY' ? 'WARN' : 'ALERT'}
            </p>
          </div>
        </div>

        {/* Category Compliance */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Category Allocation Compliance</h4>
          <div className="space-y-3">
            {(['BUSINESS', 'TECH', 'SUPPORT'] as const).map((category) => {
              const compliance = categoryCompliance[category];
              const categoryColors: Record<string, { bg: string; text: string }> = {
                BUSINESS: { bg: 'bg-blue-500', text: 'text-blue-400' },
                TECH: { bg: 'bg-sky-500', text: 'text-sky-400' },
                SUPPORT: { bg: 'bg-orange-500', text: 'text-orange-400' },
              };
              const colors = categoryColors[category];

              return (
                <div key={category} className="flex items-center gap-4">
                  <div className="w-20 text-xs font-medium text-muted-foreground">{category}</div>
                  <div className="flex-1">
                    <div className="h-2 bg-[var(--bg-hover)]/50 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${colors.bg} rounded-full transition-all duration-300`}
                        style={{ width: `${Math.min(compliance.actualPercent, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-16 text-right">
                    <span className={colors.text}>{formatPercent(compliance.actualPercent, { decimals: 0 })}</span>
                    <span className="text-gray-500 text-xs ml-1">/ {compliance.targetPercent}%</span>
                  </div>
                  <div className="w-6">
                    {compliance.compliant ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Target line indicator */}
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-block w-3 h-0.5 bg-[var(--border-primary)]" />
            <span>Target: 75% Business / 15% Tech / 10% Support</span>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

export default SupplyDemandWidget;
